import React, { useState, useEffect } from 'react';
import { UserIcon, ClockIcon } from '@heroicons/react/24/outline';
import { dashboardService } from '../../services/dashboardService';

interface MostActiveUser {
  userId: string;
  userName: string;
  email: string;
  loginCount: number;
  documentsAccessed: number;
  searchesPerformed: number;
  lastActivity: string;
}

export const LoggedUsers: React.FC = () => {
  const [users, setUsers] = useState<MostActiveUser[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchMostActiveUsers();
    
    // Atualizar a cada 5 minutos
    const interval = setInterval(fetchMostActiveUsers, 5 * 60 * 1000);
    
    return () => clearInterval(interval);
  }, []);

  const fetchMostActiveUsers = async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      // Buscar usuários mais ativos (top 10 para ter mais opções)
      const data = await dashboardService.getMostActiveUsers(10);
      
      // Filtrar apenas usuários com logins > 0
      const usersWithLogins = data.filter(user => user.loginCount > 0);
      setUsers(usersWithLogins);
    } catch (err) {
      console.error('Erro ao buscar usuários mais ativos:', err);
      setError('Não foi possível carregar os usuários mais ativos.');
    } finally {
      setIsLoading(false);
    }
  };

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(part => part[0])
      .join('')
      .toUpperCase()
      .substring(0, 2);
  };

  const getAvatarColor = (userId: string) => {
    // Gerar uma cor baseada no ID do usuário para consistência
    const colors = [
      'bg-blue-500',
      'bg-green-500',
      'bg-purple-500',
      'bg-yellow-500',
      'bg-red-500',
      'bg-indigo-500',
      'bg-pink-500',
      'bg-teal-500'
    ];
    
    const index = userId.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0) % colors.length;
    return colors[index];
  };

  const formatLastLogin = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-lg font-semibold">Usuários Mais Ativos</h2>
        <button 
          onClick={fetchMostActiveUsers}
          className="text-xs text-blue-600 hover:text-blue-800 font-medium"
        >
          Atualizar
        </button>
      </div>
      
      {isLoading ? (
        <div className="flex justify-center items-center h-40">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      ) : error ? (
        <div className="text-center text-red-500 py-4">{error}</div>
      ) : users.length === 0 ? (
        <div className="text-center text-gray-500 py-4">Nenhum usuário com login encontrado</div>
      ) : (
        <div className="max-h-80 overflow-y-auto">
          <ul className="space-y-3">
            {users
              .sort((a, b) => b.loginCount - a.loginCount)
              .slice(0, 6) // Mostrar apenas os 6 primeiros
              .map((user, index) => (
              <li key={user.userId} className="flex items-center p-3 rounded-lg hover:bg-gray-50 border-l-4 border-blue-500">
                <div className="flex items-center flex-1 min-w-0">
                  <div className={`w-10 h-10 rounded-full mr-3 flex items-center justify-center text-white flex-shrink-0 ${getAvatarColor(user.userId)}`}>
                    {getInitials(user.userName)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <p className="font-medium text-sm truncate" title={user.userName}>
                        {user.userName}
                      </p>
                      {index === 0 && (
                        <span className="text-xs bg-yellow-100 text-yellow-800 px-2 py-1 rounded-full flex-shrink-0">🥇</span>
                      )}
                      {index === 1 && (
                        <span className="text-xs bg-gray-100 text-gray-800 px-2 py-1 rounded-full flex-shrink-0">🥈</span>
                      )}
                      {index === 2 && (
                        <span className="text-xs bg-orange-100 text-orange-800 px-2 py-1 rounded-full flex-shrink-0">🥉</span>
                      )}
                    </div>
                    <p className="text-xs text-gray-500 truncate mb-1" title={user.email}>
                      {user.email}
                    </p>
                    <div className="flex items-center text-xs text-gray-500">
                      <ClockIcon className="h-3 w-3 mr-1 flex-shrink-0" />
                      <span className="truncate" title={formatLastLogin(user.lastActivity)}>
                        {formatLastLogin(user.lastActivity)}
                      </span>
                    </div>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
      
      <div className="mt-4 pt-3 border-t border-gray-200">
        <p className="text-xs text-gray-500 text-center">
          Baseado em logins do último mês • Atualiza a cada 5 minutos
        </p>
      </div>
    </div>
  );
};